'use client';

import { ApiKeys, fetcher } from '@insta-club/api';
import { Toman } from '@insta-club/components/icons';
import { GatewaysSkeleton } from '@insta-club/components/skeleton';
import { Gateway, GateWaysResponse } from '@insta-club/models';
import { useSessionStore } from '@insta-club/state';
import { t } from '@insta-club/translations';
import { RadioButton } from '@insta-club/ui';
import { separator } from '@insta-club/utilities';
import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

interface Props {
    withWallet?: boolean;
    onChange: (gateway: string) => void;
}

const fetchGateWays = (url: string) => fetcher(url, 'dashboard', 'GET');

const Gateways: React.FC<Props> = ({ onChange, withWallet = true }) => {
    const walletBalance = useSessionStore((state) => state.session?.balance) ?? 0;
    const { data, isLoading } = useSWR<GateWaysResponse>(ApiKeys.GET_GATEWAYS, fetchGateWays);

    const gateWays = useMemo(() => data?.data ?? [], [data]);

    const defaultSelected = useMemo(() => {
        if (withWallet && walletBalance > 0) return 'wallet';
        return gateWays[0]?.gateway ?? null;
    }, [gateWays, walletBalance, withWallet]);

    if (isLoading) return <GatewaysSkeleton />;

    if (!gateWays.length) return null;

    return (
        <GateWaysList
            gateWays={gateWays}
            withWallet={withWallet}
            walletBalance={walletBalance}
            defaultSelected={defaultSelected}
            onChange={onChange}
        />
    );
};

interface ListProps extends Props {
    gateWays: Gateway[];
    walletBalance: number;
    defaultSelected: string | null;
}

const GateWaysList: React.FC<ListProps> = ({ gateWays, withWallet, walletBalance, defaultSelected, onChange }) => {
    const [selected, setSelected] = useState<string | null>(null);

    useEffect(() => {
        if (defaultSelected) {
            setSelected(defaultSelected);
            onChange(defaultSelected);
        }
    }, [defaultSelected, onChange]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSelected(value);
        onChange(value);
    };

    return (
        <div className='rounded-xl bg-gray-100 p-4'>
            <h4 className='mb-4 text-base font-semibold text-gray-700'>{t('choose_payment_method')}</h4>

            <div className='space-y-3'>
                {gateWays.map(({ gateway, name, description }) => (
                    <RadioButton
                        key={gateway}
                        name='gateway'
                        value={gateway}
                        label={name}
                        description={description ?? t('online_payment')}
                        checked={selected === gateway}
                        onChange={handleChange}
                    />
                ))}

                {withWallet && (
                    <RadioButton
                        name='gateway'
                        value='wallet'
                        label={t('pay_with_wallet')}
                        disabled={walletBalance <= 0}
                        checked={selected === 'wallet'}
                        onChange={handleChange}
                        description={
                            <div className='flex items-center gap-1'>
                                <span className='text-sm text-gray-500'>{t('inventory')}</span>
                                <div className='flex items-center gap-0.5'>
                                    <span className='text-sm text-gray-800'>{separator(walletBalance)}</span>
                                    <Toman width={16} height={16} />
                                </div>
                            </div>
                        }
                    />
                )}
            </div>
        </div>
    );
};

export default Gateways;

